/**
 * components/hire/formKit.tsx
 * Shared premium form primitives for the create/edit modals (New job · Ingest
 * candidate). Pure-grayscale design system — "premium" comes from structure,
 * focus rings, spacing and iconography, never from hue.
 *
 *  - Section       — an icon-chip header + hairline that groups related fields.
 *  - Field         — label (with optional required star + hint) over a control.
 *  - PremiumInput   — TextInput with an animated focus ring (border + soft glow).
 *  - Segmented     — a segmented control (used for the remote-policy toggle).
 *
 * Both modals share these, so the two cards read as one consistent, elevated
 * surface.
 */
import React from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { web } from '../../utils/web';
import { Icon } from '../ui';
import type { IconName } from '../icon';

/* ── Section ──────────────────────────────────────────────────────────── */
export function Section({
  icon, title, hint, children, first, delay,
}: { icon: IconName; title: string; hint?: string; children: React.ReactNode; first?: boolean; delay?: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  // Staggered entrance (web only, inert on native) — the sections settle in
  // sequence as the card opens instead of all appearing at once.
  const rise = delay == null
    ? null
    : web({ animation: 'sk-rise 320ms cubic-bezier(.2,.8,.2,1) both', animationDelay: `${delay}ms` });
  return (
    <View style={[{ gap: 12 }, rise]}>
      {!first && <View style={{ height: 1, backgroundColor: colors.line, marginTop: 2 }} />}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
        <View
          style={{
            width: 24, height: 24, borderRadius: 7, alignItems: 'center', justifyContent: 'center',
            backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line,
          }}
        >
          <Icon name={icon} size={13} color={colors.ink2} />
        </View>
        <Text style={{ fontSize: 11, fontWeight: fontWeight.semibold, letterSpacing: 0.5, textTransform: 'uppercase', color: colors.ink3 }}>
          {title}
        </Text>
        {hint ? <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginLeft: 'auto' }} numberOfLines={1}>{hint}</Text> : null}
      </View>
      {children}
    </View>
  );
}

/* ── Field (label + control) ──────────────────────────────────────────── */
export function Field({
  label, required, hint, upper, children,
}: { label: string; required?: boolean; hint?: string; upper?: boolean; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const labelStyle = upper
    ? { fontSize: 12, color: colors.ink3, fontWeight: fontWeight.bold as any, letterSpacing: 1.2, textTransform: 'uppercase' as const }
    : { fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium as any, letterSpacing: -0.1 };
  return (
    <View style={{ gap: 6, flex: 1, minWidth: 0 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <Text style={labelStyle}>
          {label}
          {required && <Text style={{ color: colors.ink, fontWeight: fontWeight.bold }}> *</Text>}
        </Text>
        {hint ? <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginLeft: 'auto' }} numberOfLines={1}>{hint}</Text> : null}
      </View>
      {children}
    </View>
  );
}

/* ── Segmented control ────────────────────────────────────────────────── */
export function Segmented<T extends string>({
  options, value, onChange,
}: { options: { key: T; label: string; icon?: IconName }[]; value: T; onChange: (v: T) => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  return (
    <View
      style={{
        flexDirection: 'row', gap: 4, padding: 4, borderRadius: radii.md,
        borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2,
      }}
    >
      {options.map((o) => {
        const on = value === o.key;
        return (
          <Pressable
            key={o.key}
            onPress={() => onChange(o.key)}
            style={[
              {
                flex: 1, height: 34, flexDirection: 'row', gap: 6, alignItems: 'center', justifyContent: 'center',
                borderRadius: radii.sm,
                backgroundColor: on ? colors.card : 'transparent',
                borderWidth: 1, borderColor: on ? colors.lineStrong : 'transparent',
              },
              web({ cursor: 'pointer', transition: 'background 140ms ease, border-color 140ms ease' }),
              Platform.OS === 'web' && on ? ({ boxShadow: '0 1px 2px rgba(0,0,0,0.10)' } as object) : null,
            ]}
          >
            {o.icon ? <Icon name={o.icon} size={13} color={on ? colors.ink : colors.ink3} /> : null}
            <Text style={{ fontSize: fontSize.sm, fontWeight: on ? fontWeight.semibold : fontWeight.medium, color: on ? colors.ink : colors.ink3 }}>
              {o.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
